import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faEnvelope, faPhone } from "@fortawesome/pro-regular-svg-icons";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { BrandsIcon } from "@/components/BrandsIcon/BrandsIcon";
import { classNames } from "@/utils/classNames";

import { CcChannelStatus } from "@/schemas/cc-recipient";

/**
 * Compact channel-availability pills (LinkedIn / email / phone), each with a
 * status dot: green *found*, red *missing*, blue *enriching*. Tailwind-only so
 * it is safe to reuse inside the Chrome extension's shadow root.
 */
export interface ChannelPillsProps {
	linkedin: CcChannelStatus;
	email: CcChannelStatus;
	phone: CcChannelStatus;
	className?: string;
}

const dotColorClasses: Record<CcChannelStatus, string> = {
	found: "bg-status-success",
	missing: "bg-status-error",
	enriching: "bg-blue-action",
};

const ChannelPill: FunctionComponent<{
	channel: "linkedin" | "email" | "phone";
	status: CcChannelStatus;
}> = ({ channel, status }) => {
	const { t } = useTranslation();

	const icon =
		channel === "linkedin" ? (
			<BrandsIcon className="text-xxs" icon={faLinkedin} />
		) : (
			<Icon className="text-xxs" icon={channel === "email" ? faEnvelope : faPhone} />
		);

	return (
		<span
			aria-label={`${t(`channelPills.${channel}`)}: ${t(`channelPills.status.${status}`)}`}
			className="inline-flex items-center gap-0.5 rounded-full border border-border-light bg-white px-1 py-0.5 text-text-meta"
			data-testid={`channel-pill-${channel}`}
			title={`${t(`channelPills.${channel}`)}: ${t(`channelPills.status.${status}`)}`}
		>
			{icon}
			<span
				className={classNames("h-1.5 w-1.5 rounded-full", dotColorClasses[status])}
				data-testid={`channel-pill-${channel}-dot-${status}`}
			/>
		</span>
	);
};

export const ChannelPills: FunctionComponent<ChannelPillsProps> = ({
	linkedin,
	email,
	phone,
	className,
}) => (
	<span className={classNames("inline-flex items-center gap-0.5", className)}>
		<ChannelPill channel="linkedin" status={linkedin} />
		<ChannelPill channel="email" status={email} />
		<ChannelPill channel="phone" status={phone} />
	</span>
);
